// Text catalog for the Bygherrerådgivning / Client consultancy service page.
// Icons live in src/pages/BygherreraadgivningPage.tsx and are aligned by index
// with the phase and value-prop arrays here.

import { type Locale } from "../config";
import { type PageMeta } from "../head";

type ValueProp = { title: string; text: string };
type Phase = { label: string; title: string; items: string[] };
type Faq = { q: string; a: string };

type BygherreraadgivningStrings = {
  meta: PageMeta;
  keywords: string;
  ogTitle: string;
  ogDescription: string;
  twTitle: string;
  twDescription: string;
  hero: { eyebrow: string; title: string; body: string; ctaPrimary: string; ctaSecondary: string };
  intro: { eyebrow: string; heading: string; p1: string; p2: string };
  why: { eyebrow: string; heading: string; intro: string; props: ValueProp[] };
  approach: { eyebrow: string; heading: string; intro: string; phases: Phase[] };
  sectors: { eyebrow: string; heading: string; intro: string; items: string[] };
  faq: { eyebrow: string; heading: string; items: Faq[] };
  cta: { heading: string; body: string; call: string; write: string };
  jsonLd: { serviceType: string; name: string; description: string; audienceType: string };
};

export const bygherreraadgivningStrings: Record<Locale, BygherreraadgivningStrings> = {
  da: {
    meta: {
      title: "Bygherrerådgivning til store byggeprojekter | MI Support",
      description:
        "Uafhængig bygherrerådgivning til komplekse byggerier. MI Support sikrer bygherrens interesser fra strategi og udbud til ibrugtagning.",
      ogImage: "https://www.misupport.dk/wp-content/uploads/MISupportIT_NTK4202_1350x900.jpg",
    },
    keywords:
      "bygherrerådgivning, bygherrerådgiver, bygherre rådgiver, uafhængig bygherrerådgivning, bygherrerådgivning København, byggerådgivning, B2B byggeri, industribyggeri, bygherrens repræsentant",
    ogTitle: "Bygherrerådgivning til store byggeprojekter | MI Support",
    ogDescription:
      "Uafhængig bygherrerådgivning til komplekse B2B-byggerier. Vi varetager bygherrens interesser gennem hele projektet, fra strategi til ibrugtagning.",
    twTitle: "Bygherrerådgivning til store byggeprojekter | MI Support",
    twDescription: "Uafhængig bygherrerådgivning til komplekse B2B-byggerier i Danmark.",
    hero: {
      eyebrow: "Vores ydelser",
      title: "Bygherrerådgivning til jeres næste projekt",
      body: "MI Support er bygherrens uafhængige rådgiver gennem hele byggeprocessen. Vi sørger for, at jeres projekt bliver bygget i den ønskede kvalitet, til rigtige pris og til tiden med kontrol over risici, bæredygtighed og myndighedskrav.",
      ctaPrimary: "Tag en uforpligtende snak",
      ctaSecondary: "Se alle ydelser",
    },
    intro: {
      eyebrow: "Bygherrens betroede partner",
      heading: "Vi varetager bygherres interesser hele vejen",
      p1: "Som bygherrerådgiver er MI Support jeres betroede partner og bindeled mellem bygherre, projekterende, entreprenører, myndigheder og andre interessenter.",
      p2: "Bygherrerådgivere fra MI Support har som regel en faglig baggrund som ingeniører, byggeledere eller projektledere fra industri-, pharma- og infrastrukturbyggerier. Det giver jer en sparringspartner, der forstår både teknik, økonomi og forretning.",
    },
    why: {
      eyebrow: "HVORFOR MI SUPPORT?",
      heading: "Tryghed, transparens og resultater",
      intro:
        "Vi er en uafhængig rådgiver med fokus på at sikre bygherrens interesser gennem hele projektet, fra strategi og udbud til aflevering og drift.",
      props: [
        {
          title: "Uafhængig rådgivning",
          text: "Vi har ingen bindinger til entreprenører eller leverandører. Vores eneste loyalitet er bygherres interesser, fra start til slut.",
        },
        {
          title: "Styr på økonomi og tid",
          text: "Vi hjælper med budgettering, tidsplaner og rapporteringsmodeller, så ledelsen og bestyrelsen til hver en tid har overblik over status, risici og handlemuligheder.",
        },
        {
          title: "Erfaring med komplekse byggerier",
          text: "Vi har gennemført projekter inden for life science, pharma, produktion, infrastruktur, kontor og offentligt byggeri, hvor der ofte er høje krav til GMP, sikkerhed og driftskritiske miljøer.",
        },
      ],
    },
    approach: {
      eyebrow: "Vores tilgang",
      heading: "Bygherrerådgivning gennem hele projektets levetid",
      intro:
        "Vi kan indgå i jeres projekt fra første idé eller koble os på i en specifik fase. Vores rådgivere arbejder altid struktureret efter en gennemprøvet metode, der sikrer styring af økonomi, tid, kvalitet, sikkerhed og bæredygtighed.",
      phases: [
        {
          label: "Fase 01",
          title: "Strategi & behovsafklaring",
          items: [
            "Forretningscase, programmering og behovsanalyse",
            "Lokationsvurdering og myndighedsdialog",
            "Risikoanalyse og scenarier for byggemodning",
            "Bæredygtigheds- og DGNB-strategi",
          ],
        },
        {
          label: "Fase 02",
          title: "Projektering & udbud",
          items: [
            "Valg af entrepriseform og samarbejdsmodel",
            "Udbudsstrategi, prækvalifikation og kontrakter",
            "Granskning af projektmateriale",
            "Budget- og tidsplanstyring",
          ],
        },
        {
          label: "Fase 03",
          title: "Udførelse & ibrugtagning",
          items: [
            "Bygherrens repræsentant på byggepladsen",
            "Økonomi-, tids- og kvalitetsopfølgning",
            "Aflevering, mangelgennemgang og commissioning",
            "Drift, garantieftersyn og overlevering",
          ],
        },
      ],
    },
    sectors: {
      eyebrow: "Brancher",
      heading: "Erfaring fra de mest krævende byggerier",
      intro:
        "Vi rådgiver bygherrer inden for sektorer, hvor der er nultolerance for fejl, og hvor økonomi, tidsplaner og driftsstabilitet er forretningskritiske.",
      items: [
        "Life science & pharma (GMP)",
        "Industri- og produktionsanlæg",
        "Logistik og datacentre",
        "Kontor- og hovedsædebyggeri",
        "Offentlige bygherrer",
        "Infrastruktur og forsyning",
      ],
    },
    faq: {
      eyebrow: "FAQ",
      heading: "Ofte stillede spørgsmål om bygherrerådgivning",
      items: [
        {
          q: "Hvad er bygherrerådgivning?",
          a: "Bygherrerådgivning er uafhængig rådgivning, der varetager bygherrens interesser i et byggeprojekt. MI Supports bygherrerådgivere er bindeleddet mellem bygherre, projekterende, entreprenører og myndigheder og andre interessenter, og vi sikrer styring af økonomi, tid, kvalitet, risici og bæredygtighed.",
        },
        {
          q: "Hvornår skal vi inddrage en bygherrerådgiver?",
          a: "Jo tidligere, jo bedre. Når bygherrerådgiveren er med fra den indledende planlægningsfase, kan vi sikre, at byggeriet matcher forretningsbehovet, at risici identificeres tidligt, og at den rigtige entrepriseform vælges. Vi kan dog også koble os på i senere faser.",
        },
        {
          q: "Hvad er forskellen på bygherrerådgivning og projektledelse?",
          a: "Bygherrerådgiveren er bygherrens uafhængige sparringspartner og repræsentant. Projektlederen står for den daglige ledelse og koordinering af projektet. Hos MI Support kan vi varetage begge roller eller arbejde tæt sammen med jeres interne projektleder.",
        },
        {
          q: "Arbejder I også som bygherres repræsentant på pladsen?",
          a: "Ja. Vores bygherrerådgivere kan fungere som bygherrens repræsentant gennem hele udførelsesfasen med tilsyn, økonomistyring, kvalitetskontrol og koordinering af aflevering, mangelgennemgang og commissioning.",
        },
      ],
    },
    cta: {
      heading: "Søger I professionel rådgivning til projektet?",
      body: "Står I overfor et stort byggeprojekt, eller er I allerede i gang og har brug for en uafhængig rådgiver, der kan styrke jeres team? Vi tager gerne en uforpligtende snak om jeres projekt og behov.",
      call: "Ring +45 5555 1212",
      write: "Skriv til os",
    },
    jsonLd: {
      serviceType: "Bygherrerådgivning",
      name: "Bygherrerådgivning",
      description:
        "Uafhængig bygherrerådgivning til jeres næste projekt, fra strategi og udbud til ibrugtagning og drift.",
      audienceType: "Bygherrer, industri, life science, offentlige bygherrer",
    },
  },

  en: {
    meta: {
      title: "Client consultancy for construction projects | MI Support",
      description:
        "Independent client consultancy for complex construction. MI Support safeguards the client's interests from strategy and tendering to commissioning.",
      ogImage: "https://www.misupport.dk/wp-content/uploads/MISupportIT_NTK4202_1350x900.jpg",
    },
    keywords:
      "client consultancy, client advisor, owner's representative, independent client consultancy, client consultancy Copenhagen, construction advisory, B2B construction, industrial construction, client's representative",
    ogTitle: "Client consultancy for large construction projects | MI Support",
    ogDescription:
      "Independent client consultancy for complex B2B construction. We safeguard the client's interests throughout the project, from strategy to commissioning.",
    twTitle: "Client consultancy for large construction projects | MI Support",
    twDescription: "Independent client consultancy for complex B2B construction in Denmark.",
    hero: {
      eyebrow: "Our services",
      title: "Client consultancy for your next project",
      body: "MI Support is the client's independent advisor throughout the construction process. We make sure your project is built to the desired quality, at the right price and on time, with control over risks, sustainability and regulatory requirements.",
      ctaPrimary: "Have a no-obligation chat",
      ctaSecondary: "See all services",
    },
    intro: {
      eyebrow: "The client's trusted partner",
      heading: "We safeguard the client's interests all the way",
      p1: "As a client advisor, MI Support is your trusted partner and the link between client, designers, contractors, authorities and other stakeholders.",
      p2: "Client advisors from MI Support usually have a professional background as engineers, construction managers or project managers from industrial, pharma and infrastructure construction. This gives you a sparring partner who understands technology, economy and business alike.",
    },
    why: {
      eyebrow: "WHY MI SUPPORT?",
      heading: "Confidence, transparency and results",
      intro:
        "We are an independent advisor focused on safeguarding the client's interests throughout the project, from strategy and tendering to handover and operation.",
      props: [
        {
          title: "Independent advice",
          text: "We have no ties to contractors or suppliers. Our only loyalty is to the client's interests, from start to finish.",
        },
        {
          title: "Control of economy and time",
          text: "We help with budgeting, schedules and reporting models, so that management and the board always have an overview of status, risks and courses of action.",
        },
        {
          title: "Experience with complex construction",
          text: "We have delivered projects within life science, pharma, production, infrastructure, office and public construction, where there are often high requirements for GMP, safety and operationally critical environments.",
        },
      ],
    },
    approach: {
      eyebrow: "Our approach",
      heading: "Client consultancy throughout the project's life cycle",
      intro:
        "We can join your project from the first idea or come on board at a specific phase. Our advisors always work in a structured way according to a proven method that ensures control of economy, time, quality, safety and sustainability.",
      phases: [
        {
          label: "Phase 01",
          title: "Strategy & needs clarification",
          items: [
            "Business case, programming and needs analysis",
            "Location assessment and authority dialogue",
            "Risk analysis and scenarios for site preparation",
            "Sustainability and DGNB strategy",
          ],
        },
        {
          label: "Phase 02",
          title: "Design & tendering",
          items: [
            "Choice of contract form and collaboration model",
            "Tender strategy, prequalification and contracts",
            "Review of project material",
            "Budget and schedule management",
          ],
        },
        {
          label: "Phase 03",
          title: "Execution & commissioning",
          items: [
            "The client's representative on the construction site",
            "Economy, schedule and quality follow-up",
            "Handover, defect review and commissioning",
            "Operation, warranty inspection and handover",
          ],
        },
      ],
    },
    sectors: {
      eyebrow: "Sectors",
      heading: "Experience from the most demanding construction",
      intro:
        "We advise clients within sectors where there is zero tolerance for errors, and where economy, schedules and operational stability are business-critical.",
      items: [
        "Life science & pharma (GMP)",
        "Industrial and production facilities",
        "Logistics and data centers",
        "Office and headquarters construction",
        "Public clients",
        "Infrastructure and utilities",
      ],
    },
    faq: {
      eyebrow: "FAQ",
      heading: "Frequently asked questions about client consultancy",
      items: [
        {
          q: "What is client consultancy?",
          a: "Client consultancy is independent advisory that safeguards the client's interests in a construction project. MI Support's client advisors are the link between client, designers, contractors and authorities and other stakeholders, and we ensure control of economy, time, quality, risks and sustainability.",
        },
        {
          q: "When should we involve a client advisor?",
          a: "The earlier, the better. When the client advisor is involved from the initial planning phase, we can ensure that the construction matches the business need, that risks are identified early, and that the right contract form is chosen. However, we can also come on board in later phases.",
        },
        {
          q: "What is the difference between client consultancy and project management?",
          a: "The client advisor is the client's independent sparring partner and representative. The project manager handles the day-to-day management and coordination of the project. At MI Support we can take on both roles or work closely with your internal project manager.",
        },
        {
          q: "Do you also act as the client's representative on site?",
          a: "Yes. Our client advisors can act as the client's representative throughout the execution phase with supervision, economic management, quality control and coordination of handover, defect review and commissioning.",
        },
      ],
    },
    cta: {
      heading: "Are you looking for professional advice for your project?",
      body: "Are you facing a large construction project, or are you already underway and need an independent advisor who can strengthen your team? We're happy to have a no-obligation chat about your project and needs.",
      call: "Call +45 5555 1212",
      write: "Write to us",
    },
    jsonLd: {
      serviceType: "Client consultancy",
      name: "Client consultancy",
      description:
        "Independent client consultancy for your next project, from strategy and tendering to commissioning and operation.",
      audienceType: "Clients, industry, life science, public clients",
    },
  },

  de: {
    meta: {
      title: "Bauherrenberatung für große Bauprojekte | MI Support",
      description:
        "Unabhängige Bauherrenberatung für komplexe Bauten. MI Support wahrt die Interessen des Bauherrn von der Strategie bis zur Inbetriebnahme.",
      ogImage: "https://www.misupport.dk/wp-content/uploads/MISupportIT_NTK4202_1350x900.jpg",
    },
    keywords:
      "Bauherrenberatung, Bauherrenberater, Bauherrenvertreter, unabhängige Bauherrenberatung, Bauherrenberatung Kopenhagen, Bauberatung, B2B-Bau, Industriebau, Vertreter des Bauherrn",
    ogTitle: "Bauherrenberatung für große Bauprojekte | MI Support",
    ogDescription:
      "Unabhängige Bauherrenberatung für komplexe B2B-Bauten. Wir wahren die Interessen des Bauherrn während des gesamten Projekts, von der Strategie bis zur Inbetriebnahme.",
    twTitle: "Bauherrenberatung für große Bauprojekte | MI Support",
    twDescription: "Unabhängige Bauherrenberatung für komplexe B2B-Bauten in Dänemark.",
    hero: {
      eyebrow: "Unsere Leistungen",
      title: "Bauherrenberatung für Ihr nächstes Projekt",
      body: "MI Support ist der unabhängige Berater des Bauherrn während des gesamten Bauprozesses. Wir sorgen dafür, dass Ihr Projekt in der gewünschten Qualität, zum richtigen Preis und termingerecht gebaut wird – mit Kontrolle über Risiken, Nachhaltigkeit und behördliche Anforderungen.",
      ctaPrimary: "Unverbindliches Gespräch führen",
      ctaSecondary: "Alle Leistungen ansehen",
    },
    intro: {
      eyebrow: "Der vertraute Partner des Bauherrn",
      heading: "Wir wahren die Interessen des Bauherrn auf dem gesamten Weg",
      p1: "Als Bauherrenberater ist MI Support Ihr vertrauter Partner und das Bindeglied zwischen Bauherr, Planern, Auftragnehmern, Behörden und weiteren Beteiligten.",
      p2: "Bauherrenberater von MI Support haben in der Regel einen fachlichen Hintergrund als Ingenieure, Bauleiter oder Projektleiter aus dem Industrie-, Pharma- und Infrastrukturbau. Das gibt Ihnen einen Sparringspartner, der sowohl Technik als auch Wirtschaftlichkeit und Geschäft versteht.",
    },
    why: {
      eyebrow: "WARUM MI SUPPORT?",
      heading: "Sicherheit, Transparenz und Ergebnisse",
      intro:
        "Wir sind ein unabhängiger Berater mit dem Fokus, die Interessen des Bauherrn während des gesamten Projekts zu wahren – von Strategie und Ausschreibung bis zur Abnahme und zum Betrieb.",
      props: [
        {
          title: "Unabhängige Beratung",
          text: "Wir haben keine Bindungen zu Auftragnehmern oder Lieferanten. Unsere einzige Loyalität gilt den Interessen des Bauherrn – von Anfang bis Ende.",
        },
        {
          title: "Kontrolle über Wirtschaftlichkeit und Zeit",
          text: "Wir helfen bei Budgetierung, Terminplänen und Berichtsmodellen, sodass Geschäftsführung und Vorstand jederzeit den Überblick über Status, Risiken und Handlungsmöglichkeiten haben.",
        },
        {
          title: "Erfahrung mit komplexen Bauten",
          text: "Wir haben Projekte in den Bereichen Life Science, Pharma, Produktion, Infrastruktur, Büro und öffentlicher Bau durchgeführt, bei denen oft hohe Anforderungen an GMP, Sicherheit und betriebskritische Umgebungen bestehen.",
        },
      ],
    },
    approach: {
      eyebrow: "Unser Ansatz",
      heading: "Bauherrenberatung über den gesamten Projektlebenszyklus",
      intro:
        "Wir können ab der ersten Idee in Ihr Projekt einsteigen oder uns in einer bestimmten Phase einklinken. Unsere Berater arbeiten stets strukturiert nach einer bewährten Methode, die die Steuerung von Wirtschaftlichkeit, Zeit, Qualität, Sicherheit und Nachhaltigkeit sicherstellt.",
      phases: [
        {
          label: "Phase 01",
          title: "Strategie & Bedarfsklärung",
          items: [
            "Business Case, Programmierung und Bedarfsanalyse",
            "Standortbewertung und Behördendialog",
            "Risikoanalyse und Szenarien für die Baureifmachung",
            "Nachhaltigkeits- und DGNB-Strategie",
          ],
        },
        {
          label: "Phase 02",
          title: "Planung & Ausschreibung",
          items: [
            "Wahl der Vergabeform und des Kooperationsmodells",
            "Ausschreibungsstrategie, Präqualifikation und Verträge",
            "Prüfung des Projektmaterials",
            "Budget- und Terminplansteuerung",
          ],
        },
        {
          label: "Phase 03",
          title: "Ausführung & Inbetriebnahme",
          items: [
            "Vertreter des Bauherrn auf der Baustelle",
            "Wirtschaftlichkeits-, Termin- und Qualitätskontrolle",
            "Abnahme, Mängelprüfung und Inbetriebnahme",
            "Betrieb, Gewährleistungsinspektion und Übergabe",
          ],
        },
      ],
    },
    sectors: {
      eyebrow: "Branchen",
      heading: "Erfahrung aus den anspruchsvollsten Bauten",
      intro:
        "Wir beraten Bauherren in Sektoren, in denen es keine Toleranz für Fehler gibt und in denen Wirtschaftlichkeit, Terminpläne und Betriebsstabilität geschäftskritisch sind.",
      items: [
        "Life Science & Pharma (GMP)",
        "Industrie- und Produktionsanlagen",
        "Logistik und Rechenzentren",
        "Büro- und Hauptsitzbau",
        "Öffentliche Bauherren",
        "Infrastruktur und Versorgung",
      ],
    },
    faq: {
      eyebrow: "FAQ",
      heading: "Häufig gestellte Fragen zur Bauherrenberatung",
      items: [
        {
          q: "Was ist Bauherrenberatung?",
          a: "Bauherrenberatung ist eine unabhängige Beratung, die die Interessen des Bauherrn in einem Bauprojekt wahrt. Die Bauherrenberater von MI Support sind das Bindeglied zwischen Bauherr, Planern, Auftragnehmern und Behörden sowie weiteren Beteiligten, und wir stellen die Steuerung von Wirtschaftlichkeit, Zeit, Qualität, Risiken und Nachhaltigkeit sicher.",
        },
        {
          q: "Wann sollten wir einen Bauherrenberater einbeziehen?",
          a: "Je früher, desto besser. Wenn der Bauherrenberater ab der ersten Planungsphase dabei ist, können wir sicherstellen, dass der Bau dem Geschäftsbedarf entspricht, dass Risiken frühzeitig erkannt werden und dass die richtige Vergabeform gewählt wird. Wir können uns jedoch auch in späteren Phasen einklinken.",
        },
        {
          q: "Was ist der Unterschied zwischen Bauherrenberatung und Projektleitung?",
          a: "Der Bauherrenberater ist der unabhängige Sparringspartner und Vertreter des Bauherrn. Der Projektleiter übernimmt die tägliche Leitung und Koordination des Projekts. Bei MI Support können wir beide Rollen übernehmen oder eng mit Ihrem internen Projektleiter zusammenarbeiten.",
        },
        {
          q: "Arbeiten Sie auch als Vertreter des Bauherrn auf der Baustelle?",
          a: "Ja. Unsere Bauherrenberater können während der gesamten Ausführungsphase als Vertreter des Bauherrn fungieren – mit Aufsicht, Wirtschaftlichkeitssteuerung, Qualitätskontrolle und Koordination von Abnahme, Mängelprüfung und Inbetriebnahme.",
        },
      ],
    },
    cta: {
      heading: "Suchen Sie professionelle Beratung für Ihr Projekt?",
      body: "Stehen Sie vor einem großen Bauprojekt oder sind Sie bereits dabei und benötigen einen unabhängigen Berater, der Ihr Team stärken kann? Wir führen gern ein unverbindliches Gespräch über Ihr Projekt und Ihre Bedürfnisse.",
      call: "Anrufen +45 5555 1212",
      write: "Schreiben Sie uns",
    },
    jsonLd: {
      serviceType: "Bauherrenberatung",
      name: "Bauherrenberatung",
      description:
        "Unabhängige Bauherrenberatung für Ihr nächstes Projekt, von Strategie und Ausschreibung bis zur Inbetriebnahme und zum Betrieb.",
      audienceType: "Bauherren, Industrie, Life Science, öffentliche Bauherren",
    },
  },
};
